import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "@/components/feature/Logo";

const NAV_LINKS = [
  { label: "首页", to: "/" },
  { label: "电视剧", to: "/category/teleplay" },
  { label: "电影", to: "/category/movie" },
  { label: "综艺", to: "/category/variety" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = scrolled || !isHome || menuOpen;

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const kw = keyword.trim();
    navigate(kw ? `/search?keyword=${encodeURIComponent(kw)}` : "/search");
    setKeyword("");
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid ? "bg-background-100/95 backdrop-blur border-b border-background-200" : "bg-gradient-to-b from-background-50/90 to-transparent"
      }`}
    >
      <div className="flex items-center gap-4 md:gap-8 h-16 px-4 md:px-8">
        <Logo />

        <nav className="hidden md:flex items-center gap-1 flex-1">
          {NAV_LINKS.map((link) => {
            const active = link.to === "/" ? location.pathname === "/" : location.pathname.startsWith(link.to);
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  active ? "text-primary-500 bg-primary-500/10" : "text-foreground-700 hover:text-foreground-950 hover:bg-background-200/70"
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        <form onSubmit={handleSearch} className="hidden md:flex items-center gap-2 ml-auto">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              type="text"
              name="navbar-keyword"
              placeholder="搜索影视作品"
              className="w-52 lg:w-64 h-9 pl-9 pr-3 rounded-md bg-background-200/80 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-600 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/40 transition-colors"
            />
          </div>
          <button
            type="submit"
            className="h-9 px-4 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((open) => !open)}
          className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md text-foreground-950 hover:bg-background-200/70 cursor-pointer"
        >
          <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-2-line text-2xl"} />
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-100/98 backdrop-blur px-4 py-4 animate-fade-in">
          <form onSubmit={handleSearch} className="flex items-center gap-2 mb-4">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="text"
                name="mobile-keyword"
                placeholder="搜索影视作品"
                className="w-full h-10 pl-9 pr-3 rounded-md bg-background-200/80 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-600 outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>
          <nav className="flex flex-col">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="py-3 px-2 text-base text-foreground-800 hover:text-primary-500 border-b border-background-200/70 last:border-0 cursor-pointer"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}